//src/hooks/useActionPlanModels.ts
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  createActionPlanModel,
  createActionPlanModelItem,
  getActionPlanModel,
  listActionPlanModels,
  setActionPlanModelItemStatus,
  setActionPlanModelStatus,
  updateActionPlanModel,
  updateActionPlanModelItem,
} from "@/services/actionPlanModels";
import type { GroCrudScope } from "@/lib/groCrudScope";
import type {
  ActionPlanModelCreateIn,
  ActionPlanModelItemCreateIn,
  ActionPlanModelItemUpdateIn,
  ActionPlanModelUpdateIn,
} from "@/types/actionPlanModels";

export const ACTION_PLAN_MODELS_QK = (
  params: { q?: string; is_active?: boolean },
  scope: GroCrudScope = "client",
) => ["action_plan_models", scope, params] as const;

export function useActionPlanModels(
  params: { q?: string; is_active?: boolean },
  scope: GroCrudScope = "client",
) {
  return useQuery({
    queryKey: ACTION_PLAN_MODELS_QK(params, scope),
    queryFn: () => listActionPlanModels(params, scope),
    staleTime: 20_000,
  });
}

export function useActionPlanModelDetail(id: string | undefined, scope: GroCrudScope = "client") {
  return useQuery({
    queryKey: ["action_plan_model_detail", scope, id],
    queryFn: () => getActionPlanModel(id as string, scope),
    enabled: !!id,
    staleTime: 20_000,
  });
}

export function useCreateActionPlanModel(scope: GroCrudScope = "client") {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ActionPlanModelCreateIn) => createActionPlanModel(body, scope),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["action_plan_models", scope] });
      qc.invalidateQueries({ queryKey: ["company_gro_templates"] });
    },
  });
}

export function useUpdateActionPlanModel(scope: GroCrudScope = "client") {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: ActionPlanModelUpdateIn }) =>
      updateActionPlanModel(id, body, scope),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ["action_plan_models", scope] });
      qc.invalidateQueries({ queryKey: ["action_plan_model_detail", scope, vars.id] });
      qc.invalidateQueries({ queryKey: ["company_gro_templates"] });
    },
  });
}

export function useSetActionPlanModelStatus(scope: GroCrudScope = "client") {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, is_active }: { id: string; is_active: boolean }) =>
      setActionPlanModelStatus(id, is_active, scope),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ["action_plan_models", scope] });
      qc.invalidateQueries({ queryKey: ["action_plan_model_detail", scope, vars.id] });
      qc.invalidateQueries({ queryKey: ["company_gro_templates"] });
    },
  });
}

export function useCreateActionPlanModelItem(scope: GroCrudScope = "client") {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ modelId, body }: { modelId: string; body: ActionPlanModelItemCreateIn }) =>
      createActionPlanModelItem(modelId, body, scope),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ["action_plan_model_detail", scope, vars.modelId] });
      qc.invalidateQueries({ queryKey: ["company_gro_templates"] });
    },
  });
}

export function useUpdateActionPlanModelItem(scope: GroCrudScope = "client") {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, body }: { itemId: string; body: ActionPlanModelItemUpdateIn }) =>
      updateActionPlanModelItem(itemId, body, scope),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["action_plan_model_detail", scope] });
      qc.invalidateQueries({ queryKey: ["company_gro_templates"] });
    },
  });
}

export function useSetActionPlanModelItemStatus(scope: GroCrudScope = "client") {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, is_active }: { itemId: string; is_active: boolean }) =>
      setActionPlanModelItemStatus(itemId, is_active, scope),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["action_plan_model_detail", scope] });
      qc.invalidateQueries({ queryKey: ["company_gro_templates"] });
    },
  });
}
